// components/integrations/shared/ApiKeyTable.tsx
import React from 'react';
import { Key, Copy, Check, RefreshCw, X, CheckCircle, Plus } from 'lucide-react';
import { ApiKey } from '../../../types/features/ApiKey';

interface ApiKeyTableProps {
  apiKeys: ApiKey[];
  loading: boolean;
  moduleLabels: Record<string, string>;
  onCopy: (text: string, keyId: string) => void;
  onToggle: (id: string) => void;
  onRegenerate: (id: string) => void;
  onDelete: (key: ApiKey) => void;
  onCreateNew: () => void;
  copiedId: string | null;
  isRegenerating: boolean;
  moduleFilter?: string;
}

export const ApiKeyTable: React.FC<ApiKeyTableProps> = ({
  apiKeys,
  loading,
  moduleLabels,
  onCopy,
  onToggle,
  onRegenerate,
  onDelete,
  onCreateNew,
  copiedId,
  isRegenerating,
  moduleFilter,
}) => {
  const filteredKeys = moduleFilter ? apiKeys.filter(k => k.module === moduleFilter) : apiKeys;

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (filteredKeys.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-sm text-slate-500">
          {moduleFilter ? `Aucune clé API créée pour ${moduleLabels[moduleFilter] || moduleFilter}` : 'Aucune clé API créée'}
        </p>
        <button onClick={onCreateNew} className="mt-2 text-primary hover:text-primary/80 text-sm font-medium">
          Créer votre première clé
        </button>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200">
            <th className="text-left py-3 px-3 font-medium text-slate-600">Nom</th>
            <th className="text-left py-3 px-3 font-medium text-slate-600">Module</th>
            <th className="text-left py-3 px-3 font-medium text-slate-600">Clé</th>
            <th className="text-left py-3 px-3 font-medium text-slate-600">Permissions</th>
            <th className="text-left py-3 px-3 font-medium text-slate-600">Statut</th>
            <th className="text-left py-3 px-3 font-medium text-slate-600">Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredKeys.map((key) => (
            <tr key={key.id} className="border-b border-slate-100 hover:bg-slate-50">
              <td className="py-3 px-3 font-medium text-slate-800">{key.name}</td>
              <td className="py-3 px-3">
                <span className="px-2 py-1 bg-slate-100 text-slate-600 rounded text-xs">
                  {moduleLabels[key.module] || key.module}
                </span>
              </td>
              <td className="py-3 px-3 font-mono text-xs text-slate-600">{key.key}</td>
              <td className="py-3 px-3">
                <div className="flex flex-wrap gap-1">
                  {key.permissions?.map((perm) => (
                    <span key={perm} className="px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded text-xs">
                      {perm}
                    </span>
                  ))}
                </div>
              </td>
              <td className="py-3 px-3">
                <span className={`px-2 py-1 rounded text-xs font-medium ${key.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                  {key.is_active ? 'Actif' : 'Inactif'}
                </span>
              </td>
              <td className="py-3 px-3">
                <div className="flex items-center gap-1">
                  <button onClick={() => onCopy(key.key, key.id)} className="p-1.5 text-slate-400 hover:text-primary hover:bg-primary/10 rounded transition-colors" title="Copier">
                    {copiedId === key.id ? <Check size={14} className="text-green-600" /> : <Copy size={14} />}
                  </button>
                  <button onClick={() => onToggle(key.id)} className="p-1.5 text-slate-400 hover:text-primary hover:bg-primary/10 rounded transition-colors" title={key.is_active ? 'Désactiver' : 'Activer'}>
                    {key.is_active ? <CheckCircle size={14} /> : <X size={14} />}
                  </button>
                  <button onClick={() => onRegenerate(key.id)} disabled={isRegenerating} className="p-1.5 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded transition-colors" title="Régénérer">
                    <RefreshCw size={14} className={isRegenerating ? 'animate-spin' : ''} />
                  </button>
                  <button onClick={() => onDelete(key)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors" title="Supprimer">
                    <X size={14} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};